import { supabase } from '../config/supabaseClient.js';
import { APP_VERSION } from '../config/version.js';

/**
 * Estado de sessão da aplicação.
 * `usuario` é a linha da tabela `usuario` (perfil GESTOR|OPERADOR), não o auth.user.
 */
export const sessao = {
  authUser: null, // auth.users (Supabase Auth)
  usuario: null, // tabela usuario (perfil, status, colaborador_id)
};

/** Faz login por e-mail/senha. @returns {Promise<{erro?:string}>} */
export async function entrar(email, senha) {
  const mail = (email || '').trim().toLowerCase();
  try {
    const { data: pode } = await supabase.rpc('login_pode', { p_email: mail });
    if (pode && pode.bloqueado) {
      const ate = pode.ate ? new Date(pode.ate) : null;
      const min = ate ? Math.max(1, Math.ceil((ate - Date.now()) / 60000)) : 15;
      return { erro: 'Muitas tentativas erradas. Tente de novo em ' + min + ' min.' };
    }
  } catch (e) { /* sem a migracao 0035, segue normal */ }
  const { error } = await supabase.auth.signInWithPassword({ email: mail, password: senha });
  if (error) {
    try { await supabase.rpc('login_falha', { p_email: mail }); } catch (e) { /* ignore */ }
    return { erro: traduzErroAuth(error.message) };
  }
  try { await supabase.rpc('login_sucesso', { p_email: mail }); } catch (e) { /* ignore */ }
  await carregarSessao();
  return {};
}

/** Encerra a sessão. */
export async function sair() {
  await supabase.auth.signOut();
  sessao.authUser = null;
  sessao.usuario = null;
}

/**
 * Carrega a sessão atual: auth.user + a linha em `usuario` (com o perfil).
 * @returns {Promise<boolean>} true se há usuário autenticado E habilitado no sistema.
 */
export async function carregarSessao() {
  const { data } = await supabase.auth.getSession();
  sessao.authUser = data?.session?.user ?? null;
  sessao.usuario = null;
  if (!sessao.authUser) return false;

  // Busca o perfil. RLS: o usuário lê o próprio registro.
  const { data: u } = await supabase
    .from('usuario')
    .select('id, perfil, status, colaborador_id')
    .eq('auth_user_id', sessao.authUser.id)
    .maybeSingle();

  if (!u || u.status !== 'ATIVO') return false; // autenticado mas sem acesso liberado
  sessao.usuario = u;
  return true;
}

/** @returns {boolean} usuário logado é GESTOR. */
export const ehGestor = () => sessao.usuario?.perfil === 'GESTOR';

// Salvaguarda: se a sessao for encerrada/revogada (token expirado, conta usada em
// outro lugar), NAO seguir mostrando telas vazias -> manda pro login.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT' && sessao.usuario) { sessao.usuario = null; sessao.authUser = null; location.reload(); }
});

/**
 * Compara a versão do código com a registrada na nuvem.
 * @returns {Promise<{desatualizado:boolean, versaoNuvem:string|null}>}
 */
export async function checarVersao() {
  const { data } = await supabase
    .from('configuracoes')
    .select('valor')
    .eq('chave', 'versaoSistema')
    .maybeSingle();
  const versaoNuvem = data?.valor ?? null;
  return { desatualizado: !!versaoNuvem && versaoNuvem !== APP_VERSION, versaoNuvem };
}

/** GESTOR ao abrir versão nova grava (cria se não existir) a versão atual na nuvem. */
export async function gravarVersaoNuvem() {
  await supabase
    .from('configuracoes')
    .upsert({ chave: 'versaoSistema', valor: APP_VERSION, updated_at: new Date().toISOString() }, { onConflict: 'chave' });
}

/** Traduz mensagens comuns do GoTrue para pt-BR. */
function traduzErroAuth(msg = '') {
  if (/invalid login credentials/i.test(msg)) return 'E-mail ou senha inválidos.';
  if (/email not confirmed/i.test(msg)) return 'E-mail ainda não confirmado.';
  return 'Não foi possível entrar. Tente novamente.';
}


